<script setup>
import { ref, reactive, watch, computed, onMounted, onUnmounted } from 'vue'
import { useConditions } from 'composables/useConditions.js'

const props = defineProps({
  questions: { type: Array, default: () => [] },
})

const { evaluateConditions, isQuestionRequired } = useConditions()

const answers     = ref({})
const uploadState = ref({})

// Pre-populate answers with their configured default value.
// Hidden fields always use their default (even empty — supports URL override pattern).
// All other supporting types pre-fill only when a non-empty default is configured.
const DEFAULT_UNSUPPORTED_TYPES = ['rating', 'file_upload', 'signature']

watch(
  () => props.questions,
  (qs) => {
    qs.forEach(q => {
      const qType   = q.question.question_type
      const details = q.question.question_details || {}

      if (qType === 'hidden') {
        answers.value[q.id] = details.default ?? ''
      } else if (!DEFAULT_UNSUPPORTED_TYPES.includes(qType) && details.default?.trim()) {
        // Only pre-fill if the answer hasn't already been set (e.g. on re-render)
        if (answers.value[q.id] === undefined || answers.value[q.id] === '') {
          answers.value[q.id] = details.default
        }
      }
    })
  },
  { immediate: true }
)

// ── Condition evaluation ──────────────────────────────────────────────────────

const visibleQuestions = computed(() =>
  props.questions
    .filter(q => {
      if (q.question.question_type === 'hidden') return true // always include hidden
      return evaluateConditions(q, answers.value) !== 'hide'
    })
    .map(q => ({
      ...q,
      _required: isQuestionRequired(q, answers.value, q.required),
    }))
)

// Clear answers for questions that just became hidden so stale data is never submitted
watch(visibleQuestions, (newVisible, oldVisible) => {
  if (!oldVisible) return
  const newIds = new Set(newVisible.map(q => q.id))
  oldVisible.forEach(q => {
    if (!newIds.has(q.id)) {
      answers.value[q.id] = undefined
    }
  })
})

// ── Block state — exposed to BookingForm.vue ──────────────────────────────────
const blockState = computed(() => {
  for (const q of props.questions) {
    if (q.question.question_type === 'hidden') continue
    const action = evaluateConditions(q, answers.value)
    if (action === 'block') {
      const message = q.conditions?.block_message?.trim() ||
        'Based on your answers, this booking type may not be the right fit. Please contact us directly.'
      return { active: true, message }
    }
  }
  return { active: false, message: '' }
})

// ── Redirect state — exposed to BookingForm.vue ───────────────────────────────
const redirectState = computed(() => {
  for (const q of props.questions) {
    if (q.question.question_type === 'hidden') continue
    const action = evaluateConditions(q, answers.value)
    if (action === 'redirect') {
      const url = q.conditions?.redirect_url?.trim()
      if (url) return { active: true, url }
    }
  }
  return { active: false, url: '' }
})

// ── Answers payload ───────────────────────────────────────────────────────────

function getAnswers() {
  return visibleQuestions.value.map(q => {
    let ans = answers.value[q.id]

    // Normalise a date_range answer where both parts are empty ("," or ",") to ""
    // so that is_answered / is_blank conditions evaluate correctly on the server.
    if (
      q.question.question_type === 'date' &&
      q.question.question_details?.date_mode === 'date_range' &&
      typeof ans === 'string' &&
      ans.split(',').every(p => p.trim() === '')
    ) {
      ans = ''
    }

    return {
      event_question_id: q.id,
      answer: Array.isArray(ans) ? ans.join(', ') : (ans ?? ''),
    }
  })
}

// ── Checkbox helper ───────────────────────────────────────────────────────────

function toggleCheckbox(questionId, option, checked) {
  if (!Array.isArray(answers.value[questionId])) answers.value[questionId] = []
  answers.value[questionId] = checked
    ? [...answers.value[questionId], option]
    : answers.value[questionId].filter(o => o !== option)
}

// ── Date range helpers ────────────────────────────────────────────────────────

const isDark = ref(document.documentElement.classList.contains('dark'))
let _darkObserver = null
onMounted(() => {
  _darkObserver = new MutationObserver(() => {
    isDark.value = document.documentElement.classList.contains('dark')
  })
  _darkObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
})
onUnmounted(() => _darkObserver?.disconnect())

function strToLocalDate(str) {
  if (!str || !str.trim()) return null
  const [y, m, d] = str.split('-').map(Number)
  return new Date(y, m - 1, d)
}

function dateToStr(date) {
  if (!date) return ''
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-')
}

function dateRangeModel(questionId) {
  const str = answers.value[questionId] || ''
  const parts = str.split(',', 2)
  return {
    start: strToLocalDate(parts[0]?.trim() || ''),
    end:   strToLocalDate(parts[1]?.trim() || ''),
  }
}

function updateDateRange(questionId, val) {
  answers.value[questionId] = `${dateToStr(val?.start)},${dateToStr(val?.end)}`
}

// ── Time options ──────────────────────────────────────────────────────────────

const TIME_OPTIONS = (() => {
  const opts = []
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hh = String(h).padStart(2, '0')
      const mm = String(m).padStart(2, '0')
      const val = `${hh}:${mm}`
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
      const period = h < 12 ? 'AM' : 'PM'
      opts.push({ value: val, label: `${h12}:${mm} ${period}` })
    }
  }
  return opts
})()

function filteredTimeOptions(details) {
  const min = details?.min_time || ''
  const max = details?.max_time || ''
  return TIME_OPTIONS.filter(o => (!min || o.value >= min) && (!max || o.value <= max))
}

// ── File upload ───────────────────────────────────────────────────────────────

const INPUT_CLASS = `w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#161b22] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`

function acceptAttr(question) {
  const types = question.question_details?.allowed_types
  if (!types?.length) return undefined
  const MIME = {
    pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg',
    png: 'image/png', doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    csv: 'text/csv', mp4: 'video/mp4', gif: 'image/gif', webp: 'image/webp', txt: 'text/plain',
  }
  return types.map(t => MIME[t] || `.${t}`).join(',')
}

async function onFileChange(e, questionId, question) {
  const file = e.target.files?.[0]
  if (!file) return

  const maxBytes = (question.question_details?.max_size_mb || 50) * 1024 * 1024
  if (file.size > maxBytes) {
    uploadState.value[questionId] = { uploading: false, filename: null, error: `File exceeds the ${question.question_details?.max_size_mb || 50} MB limit` }
    answers.value[questionId] = ''
    return
  }

  uploadState.value[questionId] = { uploading: true, filename: null, error: null }
  answers.value[questionId] = ''

  const body = new FormData()
  body.append('file', file)

  try {
    const res  = await fetch('/api/v1/public/uploads', { method: 'POST', body })
    const json = await res.json()
    if (json.success) {
      answers.value[questionId]     = json.data.signed_id
      uploadState.value[questionId] = { uploading: false, filename: json.data.filename, error: null }
    } else {
      uploadState.value[questionId] = { uploading: false, filename: null, error: json.message || 'Upload failed' }
    }
  } catch {
    uploadState.value[questionId] = { uploading: false, filename: null, error: 'Upload failed. Please try again.' }
  }
}

// ── Signature pad ─────────────────────────────────────────────────────────────

const signatureCanvases = {}
const signatureDrawing  = reactive({})

function onSignatureRef(el, qid) {
  if (el) signatureCanvases[qid] = el
}

function getSigPos(e, canvas) {
  const rect = canvas.getBoundingClientRect()
  const src  = e.touches ? e.touches[0] : e
  return {
    x: (src.clientX - rect.left) * (canvas.width  / rect.width),
    y: (src.clientY - rect.top)  * (canvas.height / rect.height),
  }
}

function onSigStart(e, qid) {
  const canvas = signatureCanvases[qid]
  if (!canvas) return
  signatureDrawing[qid] = true
  const ctx = canvas.getContext('2d')
  ctx.strokeStyle = '#111827'
  ctx.lineWidth   = 2.5
  ctx.lineCap     = 'round'
  ctx.lineJoin    = 'round'
  ctx.beginPath()
  const pos = getSigPos(e, canvas)
  ctx.moveTo(pos.x, pos.y)
}

function onSigMove(e, qid) {
  if (!signatureDrawing[qid]) return
  const canvas = signatureCanvases[qid]
  if (!canvas) return
  const ctx = canvas.getContext('2d')
  const pos = getSigPos(e, canvas)
  ctx.lineTo(pos.x, pos.y)
  ctx.stroke()
}

function onSigEnd(e, qid) {
  if (!signatureDrawing[qid]) return
  signatureDrawing[qid] = false
  const canvas = signatureCanvases[qid]
  if (!canvas) return
  answers.value[qid] = canvas.toDataURL('image/png')
}

function clearSig(qid) {
  const canvas = signatureCanvases[qid]
  if (!canvas) return
  canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height)
  answers.value[qid] = ''
}

defineExpose({ getAnswers, blockState, redirectState })
</script>

<template>
  <div class="space-y-5">
    <TransitionGroup name="fq">
      <div v-for="q in visibleQuestions" :key="q.id" class="fq-item">

        <!-- Hidden — never rendered -->
        <template v-if="q.question.question_type === 'hidden'" />

        <!-- One-line -->
        <div v-else-if="q.question.question_type === 'one_line'">
          <div class="flex items-baseline justify-between mb-1.5">
            <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              {{ q.question.title }}<span v-if="q._required" class="text-red-400 ml-0.5">*</span>
            </label>
            <span v-if="q.question.question_details?.max_length"
              class="text-[11px] tabular-nums"
              :class="(answers[q.id] || '').length > q.question.question_details.max_length
                ? 'text-red-500'
                : 'text-gray-400 dark:text-gray-500'">
              {{ (answers[q.id] || '').length }}/{{ q.question.question_details.max_length }}
            </span>
          </div>
          <input v-model="answers[q.id]"
            type="text"
            :placeholder="q.question.question_details?.placeholder || q.question.title"
            :required="q._required"
            :minlength="q.question.question_details?.min_length || undefined"
            :maxlength="q.question.question_details?.max_length || undefined"
            :class="INPUT_CLASS" />
          <p v-if="q.question.question_details?.min_length" class="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
            Minimum {{ q.question.question_details.min_length }} characters
          </p>
        </div>

        <!-- Multi-line -->
        <div v-else-if="q.question.question_type === 'multi_line'">
          <div class="flex items-baseline justify-between mb-1.5">
            <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              {{ q.question.title }}<span v-if="q._required" class="text-red-400 ml-0.5">*</span>
            </label>
            <span v-if="q.question.question_details?.max_length"
              class="text-[11px] tabular-nums"
              :class="(answers[q.id] || '').length > q.question.question_details.max_length
                ? 'text-red-500'
                : 'text-gray-400 dark:text-gray-500'">
              {{ (answers[q.id] || '').length }}/{{ q.question.question_details.max_length }}
            </span>
          </div>
          <textarea v-model="answers[q.id]"
            :placeholder="q.question.question_details?.placeholder || q.question.title"
            :required="q._required"
            :minlength="q.question.question_details?.min_length || undefined"
            :maxlength="q.question.question_details?.max_length || undefined"
            rows="3"
            class="w-full px-3.5 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#161b22] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all leading-relaxed" />
          <p v-if="q.question.question_details?.min_length" class="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
            Minimum {{ q.question.question_details.min_length }} characters
          </p>
        </div>

        <!-- Phone -->
        <div v-else-if="q.question.question_type === 'phone_number'">
          <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            {{ q.question.title }}<span v-if="q._required" class="text-red-400 ml-0.5">*</span>
          </label>
          <input v-model="answers[q.id]" type="tel"
            :placeholder="q.question.question_details?.placeholder || q.question.title"
            :required="q._required" :class="INPUT_CLASS" />
        </div>

        <!-- Email -->
        <div v-else-if="q.question.question_type === 'email'">
          <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            {{ q.question.title }}<span v-if="q._required" class="text-red-400 ml-0.5">*</span>
          </label>
          <input v-model="answers[q.id]" type="email"
            :placeholder="q.question.question_details?.placeholder || q.question.title"
            :required="q._required" :class="INPUT_CLASS" />
        </div>

        <!-- Number -->
        <div v-else-if="q.question.question_type === 'number'">
          <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            {{ q.question.title }}<span v-if="q._required" class="text-red-400 ml-0.5">*</span>
          </label>
          <input v-model="answers[q.id]" type="number"
            :min="q.question.question_details?.min ?? undefined"
            :max="q.question.question_details?.max ?? undefined"
            :placeholder="q.question.question_details?.placeholder || q.question.title"
            :required="q._required" :class="INPUT_CLASS" />
        </div>

        <!-- Boolean (Yes / No) -->
        <div v-else-if="q.question.question_type === 'boolean'">
          <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            {{ q.question.title }}<span v-if="q._required" class="text-red-400 ml-0.5">*</span>
          </p>
          <div class="flex items-center gap-5">
            <label class="flex items-center gap-2 cursor-pointer">
              <input type="radio" :name="`question_${q.id}`" value="yes" v-model="answers[q.id]" :required="q._required"
                class="text-blue-600 focus:ring-0 border-gray-300 dark:border-gray-600" />
              <span class="text-sm text-gray-700 dark:text-gray-300">Yes</span>
            </label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input type="radio" :name="`question_${q.id}`" value="no" v-model="answers[q.id]"
                class="text-blue-600 focus:ring-0 border-gray-300 dark:border-gray-600" />
              <span class="text-sm text-gray-700 dark:text-gray-300">No</span>
            </label>
          </div>
        </div>

        <!-- URL -->
        <div v-else-if="q.question.question_type === 'url'">
          <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            {{ q.question.title }}<span v-if="q._required" class="text-red-400 ml-0.5">*</span>
          </label>
          <input v-model="answers[q.id]" type="url"
            :placeholder="q.question.question_details?.placeholder || 'https://'"
            :required="q._required" :class="INPUT_CLASS" />
        </div>

        <!-- Date (with sub-modes: date, date_range, time, datetime) -->
        <div v-else-if="q.question.question_type === 'date'">
          <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            {{ q.question.title }}<span v-if="q._required" class="text-red-400 ml-0.5">*</span>
          </label>

          <!-- date (default) -->
          <input v-if="!q.question.question_details?.date_mode || q.question.question_details.date_mode === 'date'"
            v-model="answers[q.id]" type="date"
            :min="q.question.question_details?.min_date ?? undefined"
            :max="q.question.question_details?.max_date ?? undefined"
            :required="q._required" :class="INPUT_CLASS" />

          <!-- date_range: single v-calendar with range selection -->
          <v-date-picker v-else-if="q.question.question_details.date_mode === 'date_range'"
            :model-value="dateRangeModel(q.id)"
            @update:model-value="val => updateDateRange(q.id, val)"
            mode="date"
            is-range
            :is-dark="isDark"
            :min-date="q.question.question_details?.min_date || undefined"
            :max-date="q.question.question_details?.max_date || undefined"
            :popover="{ visibility: 'click' }">
            <template #default="{ inputValue, inputEvents }">
              <div class="grid grid-cols-2 gap-2.5">
                <div class="relative">
                  <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500 pointer-events-none" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
                  </svg>
                  <input :value="inputValue.start" v-on="inputEvents.start"
                    placeholder="Start date" readonly :required="q._required"
                    class="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#161b22] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer" />
                </div>
                <div class="relative">
                  <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500 pointer-events-none" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
                  </svg>
                  <input :value="inputValue.end" v-on="inputEvents.end"
                    placeholder="End date" readonly
                    class="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#161b22] text-gray-900 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer" />
                </div>
              </div>
            </template>
          </v-date-picker>

          <!-- time: dropdown with 30-minute slots -->
          <select v-else-if="q.question.question_details.date_mode === 'time'"
            v-model="answers[q.id]" :required="q._required"
            class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#161b22] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer">
            <option value="" disabled>Select a time</option>
            <option v-for="opt in filteredTimeOptions(q.question.question_details)" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>

          <!-- datetime -->
          <input v-else-if="q.question.question_details.date_mode === 'datetime'"
            v-model="answers[q.id]" type="datetime-local"
            :required="q._required" :class="INPUT_CLASS" />
        </div>

        <!-- Dropdown -->
        <div v-else-if="q.question.question_type === 'drop_down' && q.question.question_details?.options?.length">
          <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            {{ q.question.title }}<span v-if="q._required" class="text-red-400 ml-0.5">*</span>
          </label>
          <select v-model="answers[q.id]" :required="q._required"
            class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#161b22] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer">
            <option value="" disabled selected>Select an option</option>
            <option v-for="opt in q.question.question_details.options" :key="opt" :value="opt">{{ opt }}</option>
          </select>
        </div>

        <!-- Radio -->
        <div v-else-if="q.question.question_type === 'radio_button' && q.question.question_details?.options?.length">
          <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            {{ q.question.title }}<span v-if="q._required" class="text-red-400 ml-0.5">*</span>
          </p>
          <div class="space-y-2">
            <label v-for="opt in q.question.question_details.options" :key="opt"
              class="flex items-center gap-2.5 cursor-pointer">
              <input type="radio" :name="`question_${q.id}`" :value="opt" v-model="answers[q.id]" :required="q._required"
                class="text-blue-600 focus:ring-0 border-gray-300 dark:border-gray-600" />
              <span class="text-sm text-gray-700 dark:text-gray-300">{{ opt }}</span>
            </label>
          </div>
        </div>

        <!-- Rating -->
        <div v-else-if="q.question.question_type === 'rating'">
          <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            {{ q.question.title }}<span v-if="q._required" class="text-red-400 ml-0.5">*</span>
          </p>
          <div v-if="(q.question.question_details?.scale ?? 5).toString() === '5'" class="flex gap-1">
            <button v-for="i in 5" :key="i" type="button"
              @click="answers[q.id] = String(i)"
              class="transition-transform active:scale-90">
              <svg class="w-8 h-8 transition-colors" :class="Number(answers[q.id]) >= i ? 'text-yellow-400' : 'text-gray-200 dark:text-gray-700'" fill="currentColor" viewBox="0 0 576 512">
                <path d="M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3l128.3-68.5 128.3 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329 542.7 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z"/>
              </svg>
            </button>
          </div>
          <div v-else class="flex flex-wrap gap-1.5">
            <button v-for="i in 11" :key="i" type="button"
              @click="answers[q.id] = String(i - 1)"
              class="w-9 h-9 rounded-lg text-sm font-semibold border transition-colors"
              :class="answers[q.id] === String(i - 1)
                ? 'bg-blue-600 border-blue-600 text-white'
                : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-blue-400'">
              {{ i - 1 }}
            </button>
          </div>
          <input type="hidden" :name="`question_${q.id}`" :value="answers[q.id] ?? ''" :required="q._required" />
        </div>

        <!-- File upload -->
        <div v-else-if="q.question.question_type === 'file_upload'">
          <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            {{ q.question.title }}<span v-if="q._required" class="text-red-400 ml-0.5">*</span>
          </label>
          <label class="flex items-center gap-3 w-full px-4 py-3 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 transition-colors group">
            <svg class="w-5 h-5 text-gray-300 dark:text-gray-600 group-hover:text-blue-400 flex-shrink-0 transition-colors" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"/>
            </svg>
            <span class="text-sm flex-1 min-w-0">
              <span v-if="uploadState[q.id]?.uploading" class="text-blue-500 dark:text-blue-400">Uploading…</span>
              <span v-else-if="uploadState[q.id]?.filename" class="text-gray-700 dark:text-gray-300 truncate block">{{ uploadState[q.id].filename }}</span>
              <span v-else class="text-gray-400 dark:text-gray-500">
                Choose file
                <span v-if="q.question.question_details?.max_size_mb" class="text-gray-300 dark:text-gray-600 ml-1">· max {{ q.question.question_details.max_size_mb }} MB</span>
              </span>
            </span>
            <svg v-if="uploadState[q.id]?.filename" @click.prevent="answers[q.id] = ''; uploadState[q.id] = null" class="w-4 h-4 text-gray-300 hover:text-red-400 flex-shrink-0 transition-colors" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
            </svg>
            <input type="file" class="sr-only" :accept="acceptAttr(q.question)"
              :required="q._required && !answers[q.id]"
              @change="e => onFileChange(e, q.id, q.question)" />
          </label>
          <p v-if="uploadState[q.id]?.error" class="text-xs text-red-500 dark:text-red-400 mt-1">{{ uploadState[q.id].error }}</p>
        </div>

        <!-- Checkbox -->
        <div v-else-if="q.question.question_type === 'check_box' && q.question.question_details?.options?.length">
          <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            {{ q.question.title }}<span v-if="q._required" class="text-red-400 ml-0.5">*</span>
          </p>
          <div class="space-y-2">
            <label v-for="opt in q.question.question_details.options" :key="opt" class="flex items-center gap-2.5 cursor-pointer">
              <input type="checkbox" :value="opt"
                :checked="(answers[q.id] ?? []).includes(opt)"
                @change="e => toggleCheckbox(q.id, opt, e.target.checked)"
                class="rounded text-blue-600 focus:ring-0 border-gray-300 dark:border-gray-600" />
              <span class="text-sm text-gray-700 dark:text-gray-300">{{ opt }}</span>
            </label>
          </div>
        </div>

        <!-- Signature -->
        <div v-else-if="q.question.question_type === 'signature'">
          <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
            {{ q.question.title }}<span v-if="q._required" class="text-red-400 ml-0.5">*</span>
          </label>
          <p v-if="q.question.question_details?.declaration_text?.trim()"
            class="text-sm text-gray-600 dark:text-gray-400 mb-3 leading-relaxed">
            {{ q.question.question_details.declaration_text }}
          </p>
          <div class="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <canvas
              :ref="el => onSignatureRef(el, q.id)"
              width="600"
              height="160"
              class="w-full block cursor-crosshair touch-none bg-white"
              @mousedown="onSigStart($event, q.id)"
              @mousemove="onSigMove($event, q.id)"
              @mouseup="onSigEnd($event, q.id)"
              @mouseleave="onSigEnd($event, q.id)"
              @touchstart="onSigStart($event, q.id)"
              @touchmove="onSigMove($event, q.id)"
              @touchend="onSigEnd($event, q.id)"
            />
            <div class="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 dark:border-gray-700/60 bg-gray-50/60 dark:bg-gray-800/40">
              <span class="text-[11px] text-gray-400 dark:text-gray-500 select-none">Draw your signature in the box above</span>
              <button type="button" @click="clearSig(q.id)"
                :disabled="!answers[q.id]"
                class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 disabled:opacity-30 transition-colors">
                Clear
              </button>
            </div>
          </div>
          <input type="hidden" :value="answers[q.id] ?? ''" :required="q._required && !answers[q.id]" />
        </div>

      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.fq-enter-active {
  transition: opacity 0.2s ease, transform 0.2s ease, max-height 0.25s ease;
  max-height: 400px;
  overflow: hidden;
}
.fq-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease, max-height 0.2s ease;
  max-height: 400px;
  overflow: hidden;
}
.fq-enter-from {
  opacity: 0;
  transform: translateY(-6px);
  max-height: 0;
}
.fq-leave-to {
  opacity: 0;
  transform: translateY(-4px);
  max-height: 0;
}
.fq-move {
  transition: transform 0.2s ease;
}
</style>
